import React from 'react';
import type {
  Env,
  Region,
} from '@internal/backstage-plugin-bidone-backend';
import type { BDirectRow } from '../../../hooks/useBDirectRows';
import { DeployedCheckbox, RegionDash, RegionTag } from './styles';

// Renders one region cell. Repo rows always render disabled — the Azure
// deployment tag is the source of truth there. Non-repo rows toggle between
// disabled (view) and interactive (edit) when the template declared a
// checkbox for the current env; cells the template didn't declare render a
// dash in both modes.
export const RegionCell: React.FC<{
  row: BDirectRow;
  region: Region;
  env: Env;
  release: string;
  editing: boolean;
  onToggleRegion?: (
    stepId: string,
    region: Region,
    env: Env,
    value: boolean,
  ) => void;
}> = ({ row, region, env, release, editing, onToggleRegion }) => {
  const lower = region.toLowerCase();

  // Top-level rule: no build for this tag → region is never editable.
  // Surface the Azure-deployed tag (when one exists) as a readonly label
  // so the user can still see what's currently in that region; otherwise
  // a dash. Applies to all repo rows — manual-tick or not — because
  // ticking a row whose build isn't even running is meaningless.
  if (row.repo && !row.build) {
    const tag = row.perRegionTag[lower];
    if (tag) return <RegionTag>{tag}</RegionTag>;
    return <RegionDash>—</RegionDash>;
  }

  // Classic Release backed rows: derive tick state from the release's
  // per-environment stages when they follow the `<env> - <region>`
  // naming convention (e.g. "Prod - AUE", "UAT - NEU"). Stage status
  // === `succeeded` → ticked; anything else (notStarted / inProgress /
  // failed / canceled) → `—`. When no matching stage is found the row
  // falls through to its default behaviour below (manual-tick override
  // or Azure-driven).
  if (row.repo && row.build?.kind === 'release' && row.build.stages?.length) {
    const envLower = env.toLowerCase();
    const stage = row.build.stages.find(s => {
      const tokens = s.name
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
      return tokens.includes(envLower) && tokens.includes(lower);
    });
    if (stage) {
      if (stage.status.toLowerCase() === 'succeeded') {
        return (
          <DeployedCheckbox checked color="primary" size="small" disabled />
        );
      }
      return <RegionDash>—</RegionDash>;
    }
  }

  // Repo rows are strictly Azure-driven by default — `--` when Azure has
  // no tag for the region. The `manualTicks` flag (set in useBDirectRows
  // for known edge cases) opts a repo row into the template-declared
  // editable path below.
  if (row.repo && !row.manualTicks) {
    const tag = row.perRegionTag[lower];
    if (!tag) return <RegionDash>—</RegionDash>;
    // Case-insensitive — Azure tag values and the release name can drift in
    // casing (e.g. `2603.0-PROD` vs `2603.0-prod`). Treat them as equivalent.
    const deployed = tag.toLowerCase() === release.toLowerCase();
    // Deployed at this release → ticked (disabled) checkbox.
    // Deployed at a different tag → drop the checkbox entirely and just
    // surface the actual tag, so the cell doesn't read as "unticked /
    // pending" — it reads as "deployed elsewhere, here's what's there".
    if (!deployed) return <RegionTag>{tag}</RegionTag>;
    return <DeployedCheckbox checked color="primary" size="small" disabled />;
  }

  const declared = row.completed[region]?.[env];
  if (declared === undefined) return <RegionDash>—</RegionDash>;
  return (
    <DeployedCheckbox
      checked={!!declared}
      color="primary"
      size="small"
      disabled={!editing}
      onChange={
        editing && onToggleRegion
          ? e => onToggleRegion(row.id, region, env, e.target.checked)
          : undefined
      }
    />
  );
};
